// Syllables inside running High Valyrian text, behind "Tavut tekstissä" (syllables/pref.ts; off by
// default): the lesson text and Kääntäjä's output wrap themselves in <SyllableScope>, and every
// <Hv> word and <Seam> inside it is shown as zal·drī·zes, the stressed syllable a step heavier.
//
// How it sits with the seam (DESIGN.md "The seam"): the seam stays the 1px weld tick with the
// ending in peacock; a syllable boundary is a middle dot in ink-2 drawn by CSS (::before, with empty
// alt text: not copied, not read aloud). Where a syllable boundary falls on the seam (a·zan|tys·sy
// → a·zan|tys·sy at "tys"), only the tick is drawn: one mark per joint, and the stronger one. Inside
// a syllable the tick splits it as before (ā·br|o·ma: the tick in "bro"). The stressed syllable is
// weight 600 in its own colour (ink, or peacock in the ending), never capitals in running text.
//
// The syllables come from the engine's phonology (stressDisplay; a borrowing Peterson stresses his
// own way, borrowedStressDisplay with `main`); a word the engine cannot split stays as written.
import { createContext, useContext, type ReactNode } from 'react';
import { borrowedStressDisplay, stressDisplay } from '../lib/engine';
import { useSyllablesPref } from './pref';
import './syllables.css';

const Scope = createContext(false);

/** Syllables on for the <Hv> and <Seam> inside, when the person has "Tavut tekstissä" on. */
export function SyllableScope({ children }: { children: ReactNode }) {
  const [on] = useSyllablesPref();
  return <Scope.Provider value={on}>{children}</Scope.Provider>;
}

/** Syllables off inside (a table inside a lesson, where the cells stay whole). */
export function NoSyllables({ children }: { children: ReactNode }) {
  return <Scope.Provider value={false}>{children}</Scope.Provider>;
}

/** Whether HV text here shows its syllables. */
export function useInlineSyllables(): boolean {
  return useContext(Scope);
}

interface Split {
  /** Start offsets of the syllables after the first. */
  cuts: number[];
  /** The stressed syllable's [start, end), or null. */
  stress: [number, number] | null;
}

const memo = new Map<string, Split | null>();

/** The word's syllable cuts, or null when it has one syllable or cannot be split exactly. */
export function syllableSplit(word: string): Split | null {
  const hit = memo.get(word);
  if (hit !== undefined) return hit;
  let out: Split | null = null;
  try {
    const b = borrowedStressDisplay(word);
    const s = b?.main ? b : stressDisplay(word);
    if (s.syllables.length > 1 && s.syllables.join('') === word) {
      const cuts: number[] = [];
      let at = 0;
      let stress: [number, number] | null = null;
      s.syllables.forEach((syl, k) => {
        if (k > 0) cuts.push(at);
        if (k === s.stressed) stress = [at, at + syl.length];
        at += syl.length;
      });
      out = { cuts, stress };
    }
  } catch {
    out = null;
  }
  if (memo.size > 5000) memo.clear();
  memo.set(word, out);
  return out;
}

/**
 * The letters of word[from, to) as syllable pieces: a piece that starts at a cut carries the dot,
 * unless that cut is `quietAt` (the seam: its tick marks the joint).
 */
export function syllablePieces(word: string, split: Split, from: number, to: number, quietAt = -1): ReactNode[] {
  const marks = [from, ...split.cuts.filter((c) => c > from && c < to), to];
  if (split.stress) for (const e of split.stress) if (e > from && e < to && !marks.includes(e)) marks.push(e);
  marks.sort((a, b) => a - b);
  const out: ReactNode[] = [];
  for (let i = 0; i < marks.length - 1; i++) {
    const a = marks[i]!;
    const b = marks[i + 1]!;
    if (a === b) continue;
    const dot = split.cuts.includes(a) && a !== quietAt;
    const on = !!split.stress && a >= split.stress[0] && b <= split.stress[1];
    const cls = ['syl', dot ? 'syl-dot' : null, on ? 'syl-on' : null].filter(Boolean).join(' ');
    out.push(
      <span key={a} className={cls}>
        {word.slice(a, b)}
      </span>,
    );
  }
  return out;
}

/** One word with its syllables (the word itself when it has one syllable). */
export function SyllableWord({ word }: { word: string }) {
  const split = syllableSplit(word);
  return <>{split ? syllablePieces(word, split, 0, word.length) : word}</>;
}

/** A word: letters and combining marks, with an inner apostrophe or hyphen (as margin/WordLink.tsx reads words). */
const WORD = /[\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*/gu;

/** Plain HV text with every word in syllables, the spaces and punctuation as they are. */
export function syllableText(text: string, keyBase = ''): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(WORD)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(<SyllableWord key={`${keyBase}s${k++}`} word={m[0]} />);
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
